import { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'
import type { CronLog, CronLogFilters, CronLogStatus } from '../types'
import { CRON_STATUS_LABELS, CRON_STATUS_COLORS, TRIGGER_SOURCE_LABELS } from '../types'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
  return `${seconds.toFixed(1)}s`
}

const columnHelper = createColumnHelper<CronLog>()

const columns = [
  columnHelper.accessor('id', { header: 'ID', size: 60 }),
  columnHelper.accessor('started_at', {
    header: 'Fecha',
    cell: ({ getValue }) => formatDate(getValue()),
  }),
  columnHelper.accessor('duration_seconds', {
    header: 'Duración',
    size: 100,
    cell: ({ getValue }) => formatDuration(getValue()),
  }),
  columnHelper.accessor('status', {
    header: 'Estado',
    size: 100,
    cell: ({ getValue }) => {
      const status = getValue()
      const colors = CRON_STATUS_COLORS[status]
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
          {CRON_STATUS_LABELS[status]}
        </span>
      )
    },
  }),
  columnHelper.accessor('trigger_source', {
    header: 'Origen',
    size: 120,
    cell: ({ getValue }) => TRIGGER_SOURCE_LABELS[getValue()] ?? getValue(),
  }),
  columnHelper.accessor((row) => row.result?.errors?.length ?? 0, {
    id: 'error_count',
    header: 'Errores',
    size: 80,
    cell: ({ getValue }) => {
      const count = getValue()
      if (count === 0) return <span className="text-gray-400">0</span>
      return <span className="text-red-600 font-medium">{count}</span>
    },
  }),
]

interface CronLogsTableProps {
  data: CronLog[]
  total: number
  page: number
  limit: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  isLoading: boolean
  filters: CronLogFilters
  onStatusFilter: (status: CronLogStatus | undefined) => void
  onClearFilters: () => void
}

export function CronLogsTable({
  data,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  isLoading,
  filters,
  onStatusFilter,
  onClearFilters,
}: CronLogsTableProps) {
  const navigate = useNavigate()
  const [localStatus, setLocalStatus] = useState<string>(filters.status ?? '')

  useEffect(() => {
    setLocalStatus(filters.status ?? '')
  }, [filters.status])

  const hasFilters = filters.status !== undefined

  const handleApply = () => {
    onStatusFilter((localStatus || undefined) as CronLogStatus | undefined)
  }

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: limit,
  }

  const pageCount = Math.ceil(total / limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageSize !== limit) {
        onLimitChange(newPagination.pageSize)
      }
      if (newPagination.pageIndex + 1 !== page) {
        onPageChange(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <select
          value={localStatus}
          onChange={(e) => setLocalStatus(e.target.value)}
          className="input w-auto text-sm"
        >
          <option value="">Estado: Todos</option>
          <option value="success">Exitoso</option>
          <option value="partial">Parcial</option>
          <option value="error">Error</option>
        </select>

        <button onClick={handleApply} className="btn btn-primary text-sm">
          Aplicar Filtros
        </button>

        {hasFilters && (
          <button onClick={onClearFilters} className="btn btn-secondary text-sm">
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron registros de ejecución
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/cron-logs/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {total} resultado{total !== 1 ? 's' : ''} - Página {page} de {pageCount || 1}
          </span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="input w-auto text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
